import React, { useEffect, useState } from 'react';
import Icon from '../components/ui/Icon';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { getUser } from '../utils/authUtil';
import AdminInsightCard from '../components/admin/AdminInsightCard';
import ReadinessSummary from '../components/admin/ReadinessSummary';
import ReadinessAnalytics from '../components/admin/ReadinessAnalytics';
import '../pages/admin/Analytics.css'; // the shared axh- token-based admin design system
import './DashboardCommandCenter.css';

// Placement Command Center — "what should the placement team know and do
// right now" (docs/UI_UX_ARCHITECTURE.md Admin Phase 5 §1-2). Leads with
// plain-language outcomes, not ERI/TPSI/CER/RCI/FFI. Every number here is
// the SAME data /reports/dashboard already computed (getCollegeDashboard) —
// this page only changes hierarchy and terminology, never re-derives a
// number or invents a second readiness formula. Deep technical detail
// remains one click away on /admin/analytics (unchanged, still available).
const READINESS_MAP = [
  { key: 'placement_ready', label: 'Ready', tone: 'success' },
  { key: 'top_performer', label: 'Strong', tone: 'info' },
  { key: 'moderate', label: 'Developing', tone: 'warning' },
  { key: 'needs_improvement', label: 'Needs Attention', tone: 'danger' },
];

const fmt = (v, suffix = '') => (v == null ? '—' : `${v}${suffix}`);

const Dashboard = () => {
  const user = getUser();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [batches, setBatches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.allSettled([
      API.get('/reports/dashboard'),
      API.get('/reports/batches/overview'),
    ]).then(([dashRes, batchRes]) => {
      if (dashRes.status === 'fulfilled') setD(dashRes.value.data);
      else setErr(dashRes.reason?.response?.data?.message || 'Failed to load dashboard');
      if (batchRes.status === 'fulfilled') setBatches(batchRes.value.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="axh-page"><div className="axh-loading"><div className="axh-spinner" />Loading college snapshot…</div></div>;
  if (err || !d) return <div className="axh-page"><div className="axh-error">{err || 'No data'}</div></div>;
  if (d.scoped === false) return <div className="axh-page"><div className="axh-empty">{d.message}</div></div>;

  const { executive: ex, college_health: h, learning: l, intervention: iv, recent_activity } = d;
  const unassessed = ex.total_students - ex.diagnosed_students;

  const readinessLevels = READINESS_MAP.map(m => ({
    ...m,
    count: (h.readiness_levels || []).find(r => r.level === m.key)?.count || 0,
  }));
  const insufficientCount = ex.total_students - readinessLevels.reduce((s, r) => s + r.count, 0);
  readinessLevels.push({ key: 'insufficient', label: 'Insufficient Evidence', tone: 'neutral', count: Math.max(0, insufficientCount) });

  return (
    <div className="axh-page">
      <div className="axh-topbar">
        <div><h1>Placement Command Center</h1><p>Welcome back, {user?.name}</p></div>
        <div className="axh-topbar-right">
          <button type="button" className="axh-btn" onClick={() => navigate('/admin/announcements')}>Manage Announcements</button>
        </div>
      </div>

      {/* ── College Snapshot ── */}
      <section className="axh-section">
        <div className="axh-section-head"><h2>College Snapshot</h2></div>
        <div className="axh-kpi-grid">
          <div className="axh-kpi"><div className="v">{ex.total_students}</div><div className="l">Total Students</div></div>
          <div className="axh-kpi"><div className="v">{ex.diagnosed_students}/{ex.total_students}</div><div className="l">Assessed</div><div className="sub">{fmt(ex.diagnostic_completion_pct, '%')} coverage</div></div>
          <div className="axh-kpi"><div className="v" style={{ color: 'var(--z-success)' }}>{ex.placement_ready}</div><div className="l">Placement Ready</div></div>
          <div className="axh-kpi"><div className="v" style={{ color: 'var(--z-danger)' }}>{ex.students_needing_attention}</div><div className="l">Needs Attention</div></div>
        </div>
        <div className="axh-card" style={{ marginTop: 14 }}>
          <h3>Placement Readiness</h3>
          <ReadinessAnalytics levels={readinessLevels} total={ex.total_students} assessed={ex.diagnosed_students} onSelectUnassessed={() => navigate('/admin/students')} />
        </div>
      </section>

      {/* ── Action Required ── */}
      <section className="axh-section">
        <div className="axh-section-head"><h2>Action Required</h2></div>
        <div className="axh-grid-2">
          {unassessed > 0 && (
            <AdminInsightCard
              tone="warning"
              what={`${unassessed} student${unassessed === 1 ? '' : 's'} need${unassessed === 1 ? 's' : ''} an assessment`}
              why="No diagnostic or aptitude attempt on record yet — their readiness can't be measured."
              actionLabel="View Students"
              onAction={() => navigate('/admin/students')}
            />
          )}
          {(iv.students || []).length > 0 && (
            <AdminInsightCard
              tone="danger"
              what={`${iv.students.length} student${iv.students.length === 1 ? '' : 's'} flagged for intervention`}
              why="Low readiness, low knowledge, or a declining trend."
              actionLabel="Review Interventions"
              onAction={() => navigate('/admin/interventions')}
            />
          )}
          {unassessed === 0 && (iv.students || []).length === 0 && (
            <AdminInsightCard tone="success" what="Nothing urgent right now" why="Every student has evidence and no one is currently flagged." />
          )}
        </div>
      </section>

      {/* ── Batch Overview ── */}
      {batches?.college_summary && (
        <section className="axh-section">
          <div className="axh-section-head">
            <h2>Batch Overview</h2>
            <p>{batches.college_summary.total_batches} batches</p>
          </div>
          <div className="axh-card">
            <ReadinessSummary
              levels={READINESS_MAP.map(m => ({ ...m, count: batches.college_summary.by_level?.[m.key] || 0 })).concat([
                { key: 'insufficient', label: 'Insufficient Evidence', tone: 'neutral', count: batches.college_summary.insufficient_evidence || 0 },
              ])}
            />
            <button type="button" className="axh-btn" style={{ marginTop: 14 }} onClick={() => navigate('/admin/batches-overview')}>
              View All Batches
            </button>
          </div>
        </section>
      )}

      {/* ── Top Areas Needing Attention ── */}
      <section className="axh-section">
        <div className="axh-section-head"><h2>Top Areas Needing Attention</h2><p>Weakest topics across diagnosed students</p></div>
        <div className="axh-card">
          {(l.weakest_topics || []).length === 0 ? (
            <div className="axh-empty">Not enough evidence yet to identify weak topics.</div>
          ) : (
            <div className="axh-bars">
              {l.weakest_topics.slice(0, 5).map((t, i) => (
                <div className="axh-bar-row" key={i}>
                  <span className="axh-bar-label">{t.topic}</span>
                  <div className="axh-bar-track"><div className="axh-bar-fill" style={{ width: `${t.accuracy}%`, background: 'var(--z-danger)' }} /></div>
                  <span className="axh-bar-val" style={{ color: 'var(--z-danger)' }}>{t.accuracy}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Recent Activity ── */}
      <section className="axh-section">
        <div className="axh-section-head"><h2>Recent Activity</h2></div>
        <div className="axh-card">
          {(recent_activity || []).length === 0 ? (
            <div className="axh-empty">No submissions yet.</div>
          ) : (
            <table className="axh-table">
              <thead><tr><th>Student</th><th>Assessment</th><th>Score</th><th>When</th></tr></thead>
              <tbody>
                {recent_activity.map((a, i) => (
                  <tr key={i}>
                    <td>{a.student_name}</td>
                    <td>{a.test_name}</td>
                    <td>{a.score_pct != null ? `${a.score_pct}%` : '—'}</td>
                    <td>{new Date(a.submitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <div className="axh-topbar-right" style={{ marginTop: 8 }}>
        <button type="button" className="axh-btn" onClick={() => navigate('/admin/analytics')}>View Detailed Analytics <Icon name="arrow-right" size={16} /></button>
      </div>
    </div>
  );
};

export default Dashboard;
